import React, { useState, useEffect, useCallback } from 'react';
import { ConfirmDelete } from '../../../components/ConfirmDelete';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton, Dialog, DialogTitle, DialogContent, TextField, CircularProgress, TablePagination, Box, AppBar, Toolbar, Typography } from '@mui/material';
import { Edit, Delete, Visibility, Add, Update, Close } from '@mui/icons-material';
import CreateMaterial from './_Create';
import EditCategoria from './_Edit';
import ViewMaterialByInstalador from './_ViewById';
import { useAuth } from '../../../context/auth';
import { getMateriales } from '../../../api/materiales';
import { getInstaladores } from '../../../api/instaladores';
import { deleteMaterial } from '../../../api/materiales';
import { UseAlert } from '../../../components/newAlert/NewAlert';
import moment from 'moment';

const Materiales = () => {
  const [materiales, setMateriales] = useState([]);
  const [instaladores, setInstaladores] = useState([]);
  const [openCreate, setOpenCreate] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [openView, setOpenView] = useState(false);
  const [selectedMaterial, setSelectedMaterial] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const { permisos } = useAuth();
  const { Prm_Mat_Crear, Prm_Mat_Modificar, Prm_Mat_Eliminar } = permisos;
  const [openConfirmDelete, setOpenConfirmDelete] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const { NewNotification } = UseAlert();

  const handleOpenDeleteConfirm = useCallback((id: number) => {
    setDeleteId(id);
    setOpenConfirmDelete(true);
  }, []);

  const handleCloseDeleteConfirm = useCallback(() => {
    setOpenConfirmDelete(false);
    setDeleteId(null);
  }, []);

  // eslint-disable-next-line react/no-danger
  useEffect(() => {
    fetchInstaladores();
    fetchMaterial();
  }, []);

  const fetchInstaladores = useCallback(async () => {
    setLoading(true);
    const response = await getInstaladores();
    if (response.isOk) {
      setInstaladores(response.data);
    }
    setLoading(false);
  }, []);

  const fetchMaterial = useCallback(async () => {
    setLoading(true);
    const response = await getMateriales();
    if (response.isOk) {
      setMateriales(response.data);
    }
    setLoading(false);
  }, [materiales]);

  const handleConfirmDelete = useCallback(async () => {
    if (deleteId !== null) {
      await deleteMaterial(deleteId)
        .then((x) => {
          if (x.isOk) {
            fetchMaterial();
            NewNotification(x.message || 'Eliminado correctamente', 'success');
            return;
          }
          NewNotification(x.message || 'Error eliminando elemento', 'error');
        })
        .finally(() => {
          handleCloseDeleteConfirm();
        });
    }
  }, [deleteId, handleCloseDeleteConfirm, materiales]);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  }, []);

  const handleChangePage = useCallback((event: unknown, newPage: number) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  }, []);

  const filteredCategorias = materiales.filter((categoria: any) => categoria.In_Nombre.toLowerCase().includes(searchTerm.toLowerCase()));

  return (
    <div style={{ marginTop: '5px' }}>
      {loading ? (
        <Box display='flex' justifyContent='center' alignItems='center' height='100%'>
          <CircularProgress />
        </Box>
      ) : (
        <div style={{ marginTop: '5px' }}>
          <Box display='flex' justifyContent='space-between' alignItems='center' mb={2}>
            <TextField label='Buscar' value={searchTerm} onChange={handleSearchChange} fullWidth size='small' />
            {Prm_Mat_Crear === 1 && (
              <IconButton onClick={() => setOpenCreate(true)} title='Nuevo' className='tool-btn'>
                <Add color='success' />
              </IconButton>
            )}
            <IconButton onClick={fetchMaterial} title='Actualizar' className='tool-btn'>
              <Update />
            </IconButton>
          </Box>
          <TableContainer component={Paper}>
            <Table size='small'>
              <TableHead>
                <TableRow>
                  <TableCell>Fecha inicio</TableCell>
                  <TableCell>Fecha fin</TableCell>
                  <TableCell>Instalador</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredCategorias.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((material: any) => (
                  <TableRow key={material.Mt_Id}>
                    <TableCell>{moment(material.Mt_Fecha_Inicio).format('DD/MM/YYYY')}</TableCell>
                    <TableCell>{moment(material.Mt_Fecha_Fin).format('DD/MM/YYYY')}</TableCell>
                    <TableCell>
                      {material.In_Nombre || ''} {material.In_Apellido || ''}
                    </TableCell>
                    <TableCell>
                      <IconButton
                        onClick={() => {
                          setSelectedMaterial(material);
                          setOpenView(true);
                        }}>
                        <Visibility color='info' />
                      </IconButton>
                      {Prm_Mat_Modificar === 1 && (
                        <IconButton
                          onClick={() => {
                            setSelectedMaterial(material);
                            setOpenEdit(true);
                          }}>
                          <Edit color='primary' />
                        </IconButton>
                      )}
                      {Prm_Mat_Eliminar === 1 && (
                        <IconButton onClick={() => handleOpenDeleteConfirm(material.Mt_Id)} title='Eliminar'>
                          <Delete color='error' />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination component='div' count={filteredCategorias.length} page={page} onPageChange={handleChangePage} rowsPerPage={rowsPerPage} onRowsPerPageChange={handleChangeRowsPerPage} />
        </div>
      )}

      <Dialog open={openCreate} onClose={() => setOpenCreate(false)} maxWidth='lg' fullWidth>
        <DialogTitle>Nuevo instalador semanal - Materiales</DialogTitle>
        <DialogContent>
          <CreateMaterial
            instaladores={instaladores}
            onClose={() => {
              setOpenCreate(false);
              fetchMaterial();
            }}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={openEdit} onClose={() => setOpenEdit(false)}>
        <DialogTitle>Editar instalador semanal - Materiales</DialogTitle>
        <DialogContent>
          <EditCategoria
            categoria={selectedMaterial}
            onClose={() => {
              setOpenEdit(false);
              fetchMaterial();
            }}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={openView} onClose={() => setOpenView(false)} fullScreen>
        <AppBar sx={{ position: 'relative' }}>
          <Toolbar>
            <IconButton edge='start' color='inherit' onClick={() => setOpenView(false)} aria-label='close'>
              <Close />
            </IconButton>
            <Typography sx={{ ml: 2, flex: 1 }} variant='h6' component='div'>
              Instalador: {selectedMaterial ? `${selectedMaterial.In_Nombre} ${selectedMaterial.In_Apellido} |  Periodo: ${moment(selectedMaterial.Mt_Fecha_Inicio).format('DD/MM/YYYY')} - ${moment(selectedMaterial.Mt_Fecha_Fin).format('DD/MM/YYYY')}` : '...'}
            </Typography>
          </Toolbar>
        </AppBar>
        <DialogContent>
          <ViewMaterialByInstalador material={selectedMaterial} onClose={() => setOpenView(false)} />
        </DialogContent>
      </Dialog>
      <ConfirmDelete open={openConfirmDelete} onClose={handleCloseDeleteConfirm} onConfirm={handleConfirmDelete} message='¿Estás seguro de que deseas eliminar este instalador semanal?' />
    </div>
  );
};

export default Materiales;
